import I18n from 'i18n-js';
import React from 'react';

import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { core_apimessages_ShippingLabelPackage } from '@reverbdotcom/commons/src/gql/graphql';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { carrierCalculatedPackageValidator, oversizeGuitarPackageWarningKey } from './carrierCalculatedPackageValidator';
import { UPS_LONGEST_SIDE_INCHES, UPS_HEAVY_PACKAGE_STICKER_REQUIRED } from './carrierCalculatedUpsPackageValidator';
import PackageInput from '../../../../discovery/shipping_labels/package_input';
import { PackageAttribute } from '../../../../discovery/shipping_labels/custom_package_inputs';

function PackageAlert({ error, warningKey }: { error: string, warningKey: string }) {
  if (!error && !warningKey) { return null; }

  let alertType;
  let message;
  if (error) {
    alertType = 'error';
    message = error;
  } else if (warningKey === 'avoidAdditionalCarrierCharges') {
    alertType = 'info';
    message = I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.packageWarnings.${warningKey}`, { tagOpen: '<b>', tagClose: '</b>' });
  } else if (warningKey === 'isOversizedDimensions') {
    alertType = 'warning';
    message = I18n.t(`discovery.sellForm.shippingSection.carrierCalculatedForm.packageWarnings.${warningKey}`);
  }

  return (
    <div className="mt-4" data-testid="package-warning">
      <RCAlertBox type={alertType} small >
        <SanitizedRender
          html={message}
        />
      </RCAlertBox>
    </div>
  );
}

interface IProps {
  packageEntry?: core_apimessages_ShippingLabelPackage;
  setPackageEntry: (core_apimessages_ShippingLabelPackage) => void;
}

export default function CarrierCalculatedGuitarForm({
  packageEntry,
  setPackageEntry,
}: IProps) {
  const carrierCalculatedPackageValidatorResponse = carrierCalculatedPackageValidator(packageEntry);
  const maxLengthInches = String(UPS_LONGEST_SIDE_INCHES);
  const maxWeightPounds = String(UPS_HEAVY_PACKAGE_STICKER_REQUIRED);
  const maxCubicInches = String(null);
  const warningKey = oversizeGuitarPackageWarningKey(packageEntry);
  const error = carrierCalculatedPackageValidatorResponse?.errorTranslationKey ?
    I18n.t(carrierCalculatedPackageValidatorResponse?.errorTranslationKey, {
      maxLengthInches,
      maxWeightPounds,
      maxCubicInches,
    }) :
    null;

  return (
    <div className="d-flex fx-dir-col gap-2">
      <div className="d-flex gap-4 fx-justify-between">
        <div className="width-50">
          <PackageInput
            dimensionKey={PackageAttribute.LENGTH}
            value={packageEntry.length.value}
            shippingPackage={packageEntry}
            measurementUnit={packageEntry.length.unit}
            onUpdate={setPackageEntry}
            roundUpUserInput
          />
        </div>
        <div className="width-50">
          <PackageInput
            dimensionKey={PackageAttribute.WIDTH}
            value={packageEntry.width.value}
            shippingPackage={packageEntry}
            measurementUnit={packageEntry.width.unit}
            onUpdate={setPackageEntry}
            roundUpUserInput
          />
        </div>
        <div className="width-50">
          <PackageInput
            dimensionKey={PackageAttribute.HEIGHT}
            value={packageEntry.height.value}
            shippingPackage={packageEntry}
            measurementUnit={packageEntry.height.unit}
            onUpdate={setPackageEntry}
            roundUpUserInput
          />
        </div>
      </div>
      <PackageInput
        dimensionKey={PackageAttribute.WEIGHT}
        value={packageEntry.weight.value}
        shippingPackage={packageEntry}
        measurementUnit={packageEntry.weight.unit}
        onUpdate={setPackageEntry}
      />

      <PackageAlert error={error} warningKey={warningKey} />
    </div>
  );
}
